import cloneDeep from 'lodash/cloneDeep';
import toInteger from 'lodash/toInteger';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { imageKit } from '@meitu/wasm-lib';
import { BaseLayerView } from '@/framework';
import { base64Kit } from '@/utils/base64-kit';
import { loadImageAsync } from '@/utils/file-kit';
import { IImageLayerProps, IMAGE_LAYER_CANVAS_ID_PREFIX, IRenderLayerProps, MASK_DEFAULT_IMG } from '@/version-types';
import { getFlipStyle, getImageStyle, getImageTransformInfo, getInnerStyle } from '../utils/layer';
import ImageEffectLayer from './image-effect-layer';
import './image-layer.less';

const ImageLayer: React.FC<IRenderLayerProps> = ({ identity, layer, isNavigator, enterLoading, exitLoading }) => {
    const fromCanvas = useRef<HTMLCanvasElement>(null);
    // const [showPreview, setShowPreview] = useState(true);
    const firstLoad = useRef(true);
    const timer = useRef<any>(null);
    const { opacity, width, height, id } = layer;
    // const previewID = `${identity}-preview-${id}`;
    const previewImgRef = useRef<HTMLImageElement>(null);

    const {
        url,
        maskURL,
        naturalWidth,
        naturalHeight,
        cutoutMaskURL,
        imageTransform: {
            widthRatio,
            heightRatio,
            flipH,
            flipV,
            offsetX: offsetXRatio,
            offsetY: offsetYRatio,
            scale,
            rotate,
        },
        minimumMask,
        borderRadius,
    } = layer.data as IImageLayerProps;
    const isCropImage = layer.__isCropImage;
    const cacheUrl = useRef('');
    const realUrl = useRef(base64Kit.getLocalUrl(url));
    const [canReplace, setCanReplace] = useState(true);
    const cacheInnerStyle = useRef<React.CSSProperties>();
    const cacheImageStyle = useRef<React.CSSProperties>();
    const filterPreview = layer.__filterPreview;
    const [updateNavigatorImageKey, setUpdateNavigatorImageKey] = useState(0);
    const { originImgWidth, originImgHeight, offsetX, offsetY } = getImageTransformInfo(layer);
    // 渲染gl画布
    const [showEnhanceCanvas, setShowEnhanceCanvas] = useState(false);
    const GLId = `GLContainer-${id}`;
    const __renderEnhanceCanvas = layer.__renderEnhanceCanvas;

    const imageStyle: React.CSSProperties = useMemo(() => {
        const style: React.CSSProperties = getImageStyle(originImgWidth, originImgHeight);

        /**
         * 如果存在抠图蒙版图片，需要添加 img 的mask 效果
         */
        if (cutoutMaskURL)
            return {
                ...style,
                WebkitMaskImage: `url(${cutoutMaskURL})`,
                WebkitMaskSize: `100% 100%`,
                WebkitMaskRepeat: `no-repeat`,
                WebkitMaskPosition: `center`,
            };

        if (!cacheImageStyle.current) {
            cacheImageStyle.current = style;
        }

        if (!canReplace) {
            const oldStyle = cloneDeep(cacheImageStyle.current);
            cacheImageStyle.current = undefined;
            return oldStyle;
        }

        return style;
    }, [cutoutMaskURL, originImgWidth, originImgHeight, canReplace]);

    useEffect(() => {
        if (__renderEnhanceCanvas) {
            const elem = document.getElementById(GLId);
            if (elem) {
                const canvas = elem.getElementsByTagName('canvas')?.[0];
                if (canvas) {
                    canvas.style.width = `${originImgWidth}px`;
                    canvas.style.height = `${originImgHeight}px`;
                }
            }
        }
    }, [__renderEnhanceCanvas, originImgWidth, originImgHeight]);

    useEffect(() => {
        if (isNavigator) {
            // 左侧导航只监测图片变更时
            realUrl.current = base64Kit.getLocalUrl(url);
            setUpdateNavigatorImageKey(updateNavigatorImageKey + 1);
            return;
        }
        if (__renderEnhanceCanvas) {
            const glCanvas = imageKit.getGLCanvas();
            if (glCanvas) {
                glCanvas.style.width = `${originImgWidth}px`;
                glCanvas.style.height = `${originImgHeight}px`;
                document.getElementById(GLId)?.appendChild(glCanvas);
            }
            fromCanvas.current?.setAttribute('ready', 'false');
            setShowEnhanceCanvas(true);
            return;
        } else {
            setShowEnhanceCanvas(false);
        }

        if (!url) {
            if (fromCanvas.current) {
                const width = fromCanvas.current.width;
                const height = fromCanvas.current.height;
                fromCanvas.current.width = width;
                fromCanvas.current.height = height;
                cacheUrl.current = '';
            }
        }

        // 滤镜预览并且不是导航器的图层不需要重新加载
        if (filterPreview) return exitLoading?.(id);

        // 绘制时会取该属性做判断
        fromCanvas.current?.setAttribute('ready', 'false');
        const convertToCanvas = (img: HTMLImageElement) => {
            const width = naturalWidth;
            const height = naturalHeight;
            if (fromCanvas.current) {
                fromCanvas.current.width = width;
                fromCanvas.current.height = height;
            }
            const ctx = fromCanvas.current?.getContext('2d');
            ctx?.clearRect(0, 0, width, height);
            ctx?.drawImage(img, 0, 0, width, height);
            fromCanvas.current?.setAttribute('ready', 'true');
            cacheUrl.current = url;

            exitLoading?.(id);
            setCanReplace(true);
            setShowEnhanceCanvas(false);
        };

        if (firstLoad.current) {
            clearTimeout(timer.current);
            enterLoading?.(id);
            loadImageAsync(url)
                .then((img) => {
                    convertToCanvas(img);
                    firstLoad.current = false;
                    if (!isNavigator) {
                        timer.current = setTimeout(() => {
                            if (previewImgRef.current) previewImgRef.current.style.opacity = '0';
                        }, 2000);
                    }
                })
                .catch(() => {
                    exitLoading?.(layer.id);
                });
        } else {
            if (url !== cacheUrl.current) {
                enterLoading?.(id);
                setCanReplace(false);
                loadImageAsync(url)
                    .then(convertToCanvas)
                    .catch(() => {
                        exitLoading?.(layer.id);
                    });
            }
        }
    }, [filterPreview, url, __renderEnhanceCanvas, isNavigator]);

    //   const baseViewStyle: React.CSSProperties = {};
    const imageInnerStyle: React.CSSProperties = useMemo(() => {
        const style = getInnerStyle(
            originImgWidth,
            originImgHeight,
            offsetX,
            offsetY,
            rotate,
            scale,
            maskURL,
            width,
            height,
        );
        if (!cacheInnerStyle.current) {
            cacheInnerStyle.current = style;
        }

        if (!canReplace) {
            const oldStyle = cloneDeep(cacheInnerStyle.current);
            cacheInnerStyle.current = undefined;
            return oldStyle;
        }

        return style;
    }, [originImgWidth, originImgHeight, offsetX, offsetY, rotate, scale, maskURL, canReplace, width, height]);

    const flipStyle = getFlipStyle(flipH, flipV);

    const imageWrapStyle: React.CSSProperties = {
        width: '100%',
        height: '100%',
    };

    // 如果蒙版存在且 当前图片不等于缺省图
    if (maskURL && maskURL.indexOf(MASK_DEFAULT_IMG().url) && !isCropImage) {
        imageWrapStyle.overflow = 'hidden';
    }

    const maskStyle = {} as React.CSSProperties;

    if (maskURL && maskURL.indexOf(MASK_DEFAULT_IMG().url) && !isCropImage) {
        maskStyle.WebkitMaskImage = `url(${maskURL})`;
        maskStyle.WebkitMaskSize = minimumMask ? '100% 100%' : 'contain';
        maskStyle.WebkitMaskRepeat = 'no-repeat';
        maskStyle.WebkitMaskPosition = 'center';
    }

    const layerContentChildrenWrapStyle = {} as React.CSSProperties;
    if (borderRadius && !borderRadius.hidden) {
        let maxWidth = layer.width / 2;
        if (layer.width > layer.height) {
            maxWidth = layer.height / 2;
        }
        layerContentChildrenWrapStyle.borderTopLeftRadius = `${toInteger(borderRadius.leftTop * maxWidth * 0.01)}px`;
        layerContentChildrenWrapStyle.borderTopRightRadius = `${toInteger(borderRadius.rightTop * maxWidth * 0.01)}px`;
        layerContentChildrenWrapStyle.borderBottomLeftRadius = `${toInteger(
            borderRadius.leftBottom * maxWidth * 0.01,
        )}px`;
        layerContentChildrenWrapStyle.borderBottomRightRadius = `${toInteger(
            borderRadius.rightBottom * maxWidth * 0.01,
        )}px`;
    }

    return (
        <>
            <ImageEffectLayer
                layer={layer}
                isNavigator={isNavigator}
                layerContentChildrenWrapStyle={layerContentChildrenWrapStyle}
            />
            <BaseLayerView
                layer={layer}
                isNavigator={isNavigator}
                isOverflowHidden={isCropImage ? false : true}
                layerContentChildrenWrapStyle={layerContentChildrenWrapStyle}
                maskStyle={maskStyle}
            >
                <div style={{ ...imageWrapStyle, opacity }}>
                    <div style={flipStyle}>
                        <div style={imageInnerStyle}>
                            {realUrl.current && (
                                <img
                                    ref={previewImgRef}
                                    crossOrigin="anonymous"
                                    style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        ...imageStyle,
                                        opacity: 1,
                                    }}
                                    src={realUrl.current}
                                    width={naturalWidth}
                                    height={naturalHeight}
                                />
                            )}

                            {!isNavigator && __renderEnhanceCanvas && (
                                <div
                                    id={GLId}
                                    style={{
                                        display: showEnhanceCanvas && !layer.__isDeformationImage ? 'block' : 'none',
                                    }}
                                ></div>
                            )}

                            {!isNavigator && (
                                <canvas
                                    style={{
                                        ...imageStyle,
                                        opacity: showEnhanceCanvas || layer.__isDeformationImage ? 0 : 1,
                                    }}
                                    id={`${IMAGE_LAYER_CANVAS_ID_PREFIX}${id}`}
                                    ref={fromCanvas}
                                    // ppt模式，取消滤镜，通过该属性获取dom
                                    data-canvas-id={`${IMAGE_LAYER_CANVAS_ID_PREFIX}${id}`}
                                ></canvas>
                            )}
                        </div>
                    </div>
                </div>
            </BaseLayerView>
        </>
    );
};

export default React.memo(ImageLayer);
